import { Card } from '@ais-ui/ui-core';
import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@ais-ui/utils';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { TargetCardContent } from './TargetCardContent';
import { TargetProvider } from './TargetContext';
import { useLayoutFlip } from '@ais-ui/custom-hooks';
import type { TargetTab } from './targetTypes';

export type { TargetTab } from './targetTypes';

const noop = () => {};

interface TargetDetailsMapProps {
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  className?: string;
  style?: React.CSSProperties;
  screenHeight?: boolean;
}

interface TargetCardProps {
  isExpanded?: boolean;
  setIsExpanded?: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

export const TargetCard = forwardRef<HTMLDivElement, TargetCardProps>(
  ({ isExpanded = true, setIsExpanded, className }, ref) => {
    return (
      <TargetProvider isExpanded={isExpanded} setIsExpanded={setIsExpanded}>
        <Card
          ref={ref}
          id="targetinfo-card"
          className={cn(
            'relative flex flex-col min-w-[464px] max-w-[464px] z-10 shadow-deferred overflow-hidden',
            // The collapsed card is a short surface floating over the map, so its
            // bottom drop-shadow is fully visible and a full shadow-md makes it
            // read as "lifted" above the docked side panels. Drop it one step
            // when collapsed. The expanded card is full-height and behaves like a
            // docked panel (bottom near the viewport edge), so it keeps shadow-md.
            isExpanded ? 'shadow-md' : 'shadow-sm',
            className,
          )}
          style={{ maxHeight: 'calc(100vh - 2rem)' }}
        >
          <TargetCardContent />
        </Card>
      </TargetProvider>
    );
  },
);

interface TargetCardStackProps {
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  className?: string;
  style?: React.CSSProperties;
}

const TargetCardStack = ({
  isExpanded,
  setIsExpanded,
  className,
  style,
}: TargetCardStackProps) => {
  const targetCardRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [targetCardHeight, setTargetCardHeight] = useState<number>(0);
  const { targetInfoDetailsList, currentTargetInfo, currentTargetType } =
    useSelectionState();

  // Animate target card expand/collapse height.
  // The card drives the deferred grid state — it starts its animation
  // immediately, and the grid recomputes 50ms later on collapse.
  useLayoutFlip(wrapperRef, [isExpanded], {
    animateHeight: true,
  });

  useEffect(() => {
    const observedElement = targetCardRef.current;

    if (!observedElement) {
      return;
    }

    const updateHeight = () => {
      setTargetCardHeight(observedElement.offsetHeight);
    };

    // Initial height measurement
    updateHeight();

    // Set up ResizeObserver to watch for height changes
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    resizeObserver.observe(observedElement);

    return () => {
      resizeObserver.unobserve(observedElement);
      resizeObserver.disconnect();
    };
  }, [
    isExpanded,
    currentTargetInfo,
    currentTargetType,
    targetInfoDetailsList.length,
  ]);

  if (!currentTargetInfo || !currentTargetType) {
    return null;
  }

  return (
    <div
      ref={wrapperRef}
      style={style}
      className={cn(
        'relative isolate max-h-screen animate-in fade-in slide-in-from-left-3 duration-200',
        isExpanded && 'h-full',
        className,
        targetInfoDetailsList.length === 2 && 'ml-3',
        targetInfoDetailsList.length >= 2 && 'ml-6',
      )}
    >
      <TargetCard
        ref={targetCardRef}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        className={cn(isExpanded && 'h-full')}
      />
      <div className="absolute top-0 left-0">
        {targetInfoDetailsList.length >= 3 && (
          <Card
            id="stack-card-2"
            className={cn(
              'absolute z-1 min-w-[464px] max-w-[464px] -top-3 -left-6 cursor-default pointer-events-auto',
              // Keep the stack one elevation step below the top card so the
              // "stacked paper" depth survives when the top card is collapsed.
              isExpanded ? 'shadow-sm' : 'shadow-xs',
            )}
            style={{
              height: targetCardHeight,
              maxHeight: 'calc(100vh - 2rem)',
            }}
          />
        )}
        {targetInfoDetailsList.length >= 2 && (
          <Card
            id="stack-card-1"
            className={cn(
              'absolute z-2 min-w-[464px] max-w-[464px] -top-1.5 -left-3 cursor-default pointer-events-auto',
              isExpanded ? 'shadow-sm' : 'shadow-xs',
            )}
            style={{
              height: targetCardHeight,
              maxHeight: 'calc(100vh - 2rem)',
            }}
          />
        )}
      </div>
    </div>
  );
};

export const TargetDetails = React.memo(
  ({ isExpanded, setIsExpanded, className, style }: TargetDetailsMapProps) => {
    const { targetInfoDetailsList, setTargetInfoDetailsList } =
      useSelectionState();

    const handleEscape = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setTargetInfoDetailsList([]);
        }
      },
      [setTargetInfoDetailsList],
    );

    useEffect(() => {
      window.addEventListener('keydown', handleEscape);

      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }, [handleEscape]);

    if (targetInfoDetailsList.length === 0) {
      return null;
    }

    return (
      <TargetCardStack
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        className={className}
        style={style}
      />
    );
  },
);

TargetDetails.displayName = 'TargetDetails';
